import { useState } from 'react'
import { Button } from '../platform/primitives/Button'
import { Panel } from '../platform/primitives/Panel'
import { emit } from '../platform/analytics/analytics'
import { analyticsCode, authErrorFor } from './errors'
import { AuthFailure } from './port'
import { useAuth } from './useAuth'
import './verifyEmailBanner.css'

/**
 * Per TAB, not per browser — the intake decision, and the whole reason this
 * key names `sessionStorage`.
 *
 * `localStorage` would dismiss the reminder forever after one click, so a user
 * who never verifies would never be reminded again and the address on the
 * account would stay unreachable for password resets and security mail.
 * `sessionStorage` costs them one dismissal per tab and nothing else.
 *
 * Exported so the test can assert WHICH storage was written. The behavioural
 * test — dismissed, remount, still gone — passes under either one.
 */
export const VERIFY_DISMISS_KEY = 'tracke5.verify-email.dismissed'

/**
 * Storage throws SecurityError when cookies are blocked, inside a sandboxed
 * iframe and under some enterprise policies. `ChromeLayout` and `ThemeProvider`
 * both carry this guard; an unguarded read here would put the throw above every
 * error boundary — a white screen for a user whose only sin is blocking
 * cookies. Blocked storage should cost the preference, not the session.
 */
function readDismissed(): boolean {
  try {
    return sessionStorage.getItem(VERIFY_DISMISS_KEY) === '1'
  } catch {
    return false
  }
}

function writeDismissed(): void {
  try {
    sessionStorage.setItem(VERIFY_DISMISS_KEY, '1')
  } catch {
    // The dismissal still applies for this render; it just will not survive a
    // navigation. Nothing worth interrupting the user over.
  }
}

/** What the last Resend did. `null` is "not asked yet". */
type Resend = { ok: true } | { ok: false; message: string } | null

/**
 * M1-R4: verification is a reminder, **not a gate**.
 *
 * The banner renders beside the page rather than in front of it, and nothing
 * here can prevent a route rendering — `ChromeLayout` mounts it as a sibling of
 * the outlet, so "not a gate" is a property of the shape rather than of a
 * branch somebody could invert. The requirement rules out blocking, and the
 * backend agrees: no endpoint checks `email_verified`.
 *
 * **Not an `AlertPanel`**, which the plan names. That surface is `--neg-soft`
 * with `--neg` ink — the app's *error* skin, which `Async` renders for a failed
 * request. An unverified address is not a failure and nothing is broken; a red
 * band across every page for a state the user is allowed to stay in overstates
 * it, and it would put the one signal that says *destructive* on a reminder.
 * A `Panel` with a hairline is the elevation this design system has.
 */
export function VerifyEmailBanner() {
  const auth = useAuth()
  const [dismissed, setDismissed] = useState(readDismissed)
  const [busy, setBusy] = useState(false)
  const [resend, setResend] = useState<Resend>(null)

  // Only from `signedIn`. `unknown` is the state on every reload before
  // Firebase reports, and rendering there would flash the banner on every page
  // load for everybody — including verified users.
  if (auth.status !== 'signedIn') return null
  if (auth.user.emailVerified) return null
  if (dismissed) return null

  async function onResend() {
    setBusy(true)
    setResend(null)
    try {
      await auth.sendVerificationEmail()
      emit('email_verification_resent', { error_code: null })
      setResend({ ok: true })
    } catch (thrown) {
      // `auth/too-many-requests` is the expected failure here — Firebase
      // throttles verification mail hard — and a silent failure means the user
      // clicks it five more times, which is exactly what the throttle is
      // counting.
      const detail = thrown instanceof AuthFailure ? thrown.detail : authErrorFor('unexpected')
      setResend({ ok: false, message: detail.message })
      emit('email_verification_resent', { error_code: analyticsCode(detail.code) })
    } finally {
      setBusy(false)
    }
  }

  function onDismiss() {
    writeDismissed()
    setDismissed(true)
  }

  return (
    <Panel className="verify-banner">
      <div className="verify-banner__row">
        <p className="verify-banner__text">
          {/* The address, because "check your email" is useless to someone with
              two who cannot remember which they signed up with. It is the
              account's own address and already on screen in the account
              menu, so naming it here reveals nothing new. */}
          You haven't verified {auth.user.email ?? 'your email address'} yet. Until you do, we
          can't email you a password reset or a security alert.
        </p>
        <Button size="panel" disabled={busy} onClick={() => void onResend()}>
          {busy ? 'Sending…' : 'Resend email'}
        </Button>
        {/* Both are the default `chip`, deliberately. There is no ghost variant,
            and `primary` is the one lime surface per screen — which belongs to
            whatever the PAGE is for, never to a reminder sitting above it. */}
        <Button size="panel" onClick={onDismiss}>
          {/* Says what it does to THIS tab. "Dismiss" alone reads as "never
              again", which is what sessionStorage deliberately does not do. */}
          Dismiss for now
        </Button>
      </div>
      {resend?.ok === true && (
        // `status`, not `alert`: nothing went wrong, and an assertive region
        // interrupts a screen-reader user to say so.
        <p className="verify-banner__note" role="status">
          Verification email sent. It can take a minute — check your spam folder too.
        </p>
      )}
      {resend?.ok === false && (
        <p className="verify-banner__error" role="alert">
          {resend.message}
        </p>
      )}
    </Panel>
  )
}
